import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { fmtDate, fmtPriceForSymbol } from './DcaUtils';
import { formatPrice, formatQty } from "@/lib/utils";
import { cx } from 'class-variance-authority';

import { CycleDetailView } from './CycleDetailView';

interface CycleHistoryPanelProps {
  botId: number;
  symbol: string;
}

export const CycleHistoryPanel: React.FC<CycleHistoryPanelProps> = ({ botId, symbol }) => {
  const [cycles, setCycles] = useState<any[]>([]);
  const [histories, setHistories] = useState<any[]>([]);
  const [historiesTotal, setHistoriesTotal] = useState(0);
  const [historiesPage, setHistoriesPage] = useState(1);
  const [historiesPer] = useState(30);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const curSym = symbol.endsWith('KRW') ? '₩' : '$';

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchCycles = useCallback(async (hPage = 1, isSilent = false) => {
    if (!botId) return;
    if (!isSilent) setLoading(true);

    try {
      const res = await fetch(`/dca/${botId}/cycles?histories_page=${hPage}`);
      const data = await res.json();

      setCycles(data.cycles || []);
      setHistories(data.histories || []);
      setHistoriesTotal(data.histories_total || 0);
      setHistoriesPage(data.histories_page || hPage);
    } catch { } finally {
      if (!isSilent) setLoading(false);
    }
  }, [botId]);

  useEffect(() => {
    fetchCycles(historiesPage);
    // 봇 변경 시 상태 초기화
    setExpandedRows({});
  }, [botId, historiesPage, fetchCycles]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-primary" />
          <span className="hidden md:block text-[11px] font-black text-foreground uppercase tracking-widest">사이클 요약</span>
          <Badge variant="outline" className="bg-primary/5 text-primary border-primary/10 text-[9px] font-bold h-4 px-1">{historiesTotal}회차</Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchCycles(historiesPage)}
          disabled={loading}
          className="h-8 px-2 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-lg transition-all"
        >
          <RefreshCw className={cx("w-3.5 h-3.5 mr-1.5", loading && "animate-spin")} />
          <span className="text-[10px] font-bold">새로고침</span>
        </Button>
      </div>
      
      {histories.length === 0 && cycles.length === 0 ? (
        <div className="py-16 text-center bg-muted/5 rounded-3xl border border-dashed border-border flex flex-col items-center gap-3">
          <Layers className="w-8 h-8 text-muted-foreground/20" />
          <p className="text-[11px] font-bold text-muted-foreground">완료된 사이클 이력이 아직 없습니다.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border shadow-sm">
          <table className="w-full text-[10px] text-left">
            <thead className="bg-muted/50 text-muted-foreground uppercase font-black tracking-wider text-[9px]">
              <tr>
                <th className="px-3 py-3">ID</th>
                <th className="px-3 py-3">상태</th>
                <th className="px-3 py-3">방향</th>
                <th className="px-3 py-3 text-right">평단 / 단계</th>
                <th className="px-3 py-3 text-right">증거금 / 수량</th>
                <th className="px-3 py-3 text-right">수익 (ROE%)</th>
                <th className="px-3 py-3 text-right">종료 시간</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {cycles.map((c: any) => (
                <React.Fragment key={`cycle-frag-${c.id}`}>
                  <tr 
                    onClick={() => toggleRow(`cycle-${c.id}`)}
                    className={cx("bg-primary/5 group cursor-pointer hover:bg-primary/10 transition-colors", expandedRows[`cycle-${c.id}`] && "bg-primary/10")}
                  >
                    <td className="px-3 py-3.5">
                      <span className="text-[10px] font-black text-primary leading-none">#{c.id}</span>
                    </td>
                    <td className="px-3 py-3.5">
                      <div className="flex flex-col gap-1 items-start">
                        <Badge className="bg-primary/20 text-primary border-none text-[8px] h-4 font-black">ACTIVE</Badge>
                        <div className="flex gap-1">
                          {c.is_live === false ? (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[7px] h-3.5 px-1 font-black">PAPER</Badge>
                          ) : (
                            <>
                              <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[7px] h-3.5 px-1 font-black">LIVE</Badge>
                              {c.use_testnet && (
                                <Badge variant="outline" className="bg-indigo-500/10 text-indigo-500 border-indigo-500/20 text-[7px] h-3.5 px-1 font-black">TESTNET</Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3.5">
                      <span className={cx("font-black", c.side === 'long' ? "text-success" : "text-danger")}>{c.side?.toUpperCase()}</span>
                    </td>
                    <td className="px-3 py-3.5 text-right font-bold">
                      <div>{curSym}{fmtPriceForSymbol(c.avg_price, symbol)}</div>
                      <div className="text-[9px] text-muted-foreground font-medium">{c.current_step}단계</div>
                    </td>
                    <td className="px-3 py-3.5 text-right">
                      <div className="font-bold text-foreground/80">{curSym}{formatPrice(c.total_cost, 2)}</div>
                      <div className="text-[9px] text-muted-foreground">{formatQty(c.total_qty, 6)} Qty</div>
                    </td>
                    <td className="px-3 py-3.5 text-right font-black italic text-muted-foreground opacity-40 text-[9px]">
                      진행 중...
                    </td>
                    <td className="px-3 py-3.5 text-right tabular-nums text-muted-foreground opacity-30">
                      -
                    </td>
                  </tr>
                  {expandedRows[`cycle-${c.id}`] && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <CycleDetailView cycleId={c.id} symbol={symbol} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {histories.map((h: any) => {
                const pnl = Number(h.pnl || 0);
                const roe = Number(h.roe || (h.total_cost > 0 ? (pnl / h.total_cost * 100) : 0));
                const rowId = `history-${h.cycle_id || h.id}`;
                return (
                  <React.Fragment key={`history-frag-${h.id}`}>
                    <tr 
                      onClick={() => h.cycle_id && toggleRow(rowId)}
                      className={cx("hover:bg-muted/30 transition-colors cursor-pointer", expandedRows[rowId] && "bg-muted/40 border-l-2 border-primary")}
                    >
                      <td className="px-3 py-3.5">
                        {h.cycle_id ? (
                          <span className="text-[10px] font-black text-muted-foreground leading-none">#{h.cycle_id}</span>
                        ) : (
                          <span className="text-[10px] font-bold text-muted-foreground/30">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex flex-col gap-1 items-start">
                          <div className="flex items-center gap-1">
                            {h.is_live === false ? (
                              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[8px] h-4 font-black">PAPER</Badge>
                            ) : (
                              <>
                                <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[8px] h-4 font-black">LIVE</Badge>
                                {h.use_testnet && (
                                  <Badge variant="outline" className="bg-indigo-500/10 text-indigo-500 border-indigo-500/20 text-[8px] h-4 font-black">TESTNET</Badge>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {h.exit_type === 'offset' ? (
                              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[8px] h-4 font-black">상계처리</Badge>
                            ) : h.exit_type === 'rebound_reduction' ? (
                              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[8px] h-4 font-black">덜어내기</Badge>
                            ) : h.exit_type === 'tp' ? (
                              <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[8px] h-4 font-black">익절완료</Badge>
                            ) : h.exit_type === 'sl' ? (
                              <Badge variant="outline" className="bg-danger/10 text-danger border-danger/20 text-[8px] h-4 font-black">손절완료</Badge>
                            ) : h.exit_type === 'sync' ? (
                              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-[8px] h-4 font-black">시스템종료</Badge>
                            ) : h.exit_type === 'manual' ? (
                              <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-[8px] h-4 font-black">수동종료</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground border-border text-[8px] h-4 font-bold">CLOSED</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={cx("font-black", h.side === 'long' ? "text-success/70" : "text-danger/70")}>{h.side?.toUpperCase()}</span>
                      </td>
                      <td className="px-3 py-3.5 text-right font-bold">
                        <div>{curSym}{fmtPriceForSymbol(h.avg_price, symbol)}</div>
                        <div className="text-[9px] text-muted-foreground font-medium">{h.current_step}단계</div>
                      </td>
                      <td className="px-3 py-3.5 text-right">
                        <div className="font-bold text-foreground/80">{curSym}{formatPrice(h.total_cost, 2)}</div>
                        <div className="text-[9px] text-muted-foreground">{formatQty(h.total_qty, 6)} Qty</div>
                      </td>
                      <td className="px-3 py-3.5 text-right font-black">
                        <div className={h.exit_type === 'offset' ? "text-amber-500" : (pnl >= 0 ? "text-success" : "text-danger")}>
                          {pnl >= 0 ? '+' : ''}{formatPrice(pnl, 2)}
                        </div>
                        <div className={cx("text-[9px] font-black", h.exit_type === 'offset' ? "text-amber-500/60" : (pnl >= 0 ? "text-success/60" : "text-danger/60"))}>
                          {roe >= 0 ? '+' : ''}{roe.toFixed(2)}%
                        </div>
                      </td>
                      <td className="px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                        {fmtDate(h.closed_at).slice(5)}
                      </td>
                    </tr>
                    {expandedRows[rowId] && h.cycle_id && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <CycleDetailView cycleId={h.cycle_id} symbol={symbol} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 사이클 이력 페이지네이션 컨트롤 */}
      {historiesTotal > historiesPer && (
        <div className="flex items-center justify-between px-2 py-2 bg-muted/10 rounded-xl mt-4">
          <div className="text-[10px] text-muted-foreground font-bold pl-2">
            총 {historiesTotal.toLocaleString()}건 중 {(historiesPage - 1) * historiesPer + 1}~{Math.min(historiesPage * historiesPer, historiesTotal)} 표시
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoriesPage((p: number) => Math.max(1, p - 1))}
              disabled={historiesPage === 1 || loading}
              className="h-7 w-7 p-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border/50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center justify-center h-7 px-3 bg-background border border-border/50 rounded-lg text-[10px] font-black text-primary">
              {historiesPage} / {Math.ceil(historiesTotal / historiesPer)}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoriesPage((p: number) => p + 1)}
              disabled={historiesPage >= Math.ceil(historiesTotal / historiesPer) || loading}
              className="h-7 w-7 p-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border/50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
